import { useState } from "react";

import {
  CreditNote,
  CREDIT_NOTE_STATUS_APPROVED_BY_SET,
  CREDIT_NOTE_STATUS_CANCELLATION_FAILED,
  CREDIT_NOTE_STATUS_CANCELLATION_REQUESTED,
  CREDIT_NOTE_STATUS_CANCELLED,
  CREDIT_NOTE_STATUS_ERROR,
  CREDIT_NOTE_STATUS_RESEND_REQUESTED,
  CREDIT_NOTE_STATUS_SET_UNKNOWN_ERROR,
  CREDIT_NOTE_STATUS_UPLOADED_TO_SET,
  formatCreditNoteNumber,
} from "../../models/credit-note";
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCollapse,
  CTooltip,
} from "@coreui/react";
import { formatUTCDateTime } from "../../utils/dates";
import { formatToCurrency } from "../../currency/format";
import { CURRENCY_CHOICES } from "../../currency/available-currencies";
import CreditNoteCrudStatus from "./CreditNoteCrudStatus";
import CreditNoteCrudActions from "./CreditNoteCrudActions";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import DocumentPrintButton from "../documents/DocumentPrintButton";
import { CREDIT_NOTES_RETRIEVE } from "../../auth/permissions";

interface CreditNoteCardProps {
  creditNote: CreditNote;
  onXMLClick: (creditNote: CreditNote) => void;
  onPDFClick: (creditNote: CreditNote) => void;
  onCancellationClick: (creditNote: CreditNote) => void;
  onCancellationRetryClick: (creditNote: CreditNote) => void;
  onCancellationRevertClick: (creditNote: CreditNote) => void;
  onReadClick: (creditNote: CreditNote) => void;
  onUpdateClick: (creditNote: CreditNote) => void;
  onDeleteClick: (creditNote: CreditNote) => void;
  onConsultByCdcClick: (creditNote: CreditNote) => void;
  onResendClick: (creditNote: CreditNote) => void;
  onSendEmailClick: (creditNote: CreditNote) => void;
  onHistoryClick: (creditNote: CreditNote) => void;
}

export function CreditNoteCard({
  creditNote,
  onXMLClick,
  onPDFClick,
  onCancellationClick,
  onCancellationRetryClick,
  onCancellationRevertClick,
  onReadClick,
  onUpdateClick,
  onDeleteClick,
  onConsultByCdcClick,
  onResendClick,
  onSendEmailClick,
  onHistoryClick,
}: CreditNoteCardProps) {
  const [open, setOpen] = useState(true);

  const getStatusColor = (status?: string): string => {
    switch (status) {
      case CREDIT_NOTE_STATUS_UPLOADED_TO_SET:
        return "warning";
      case CREDIT_NOTE_STATUS_ERROR:
        return "danger";
      case CREDIT_NOTE_STATUS_SET_UNKNOWN_ERROR:
        return "danger";
      case CREDIT_NOTE_STATUS_CANCELLATION_FAILED:
        return "danger";
      case CREDIT_NOTE_STATUS_APPROVED_BY_SET:
        return "success";
      case CREDIT_NOTE_STATUS_CANCELLATION_REQUESTED:
        return "light";
      case CREDIT_NOTE_STATUS_CANCELLED:
        return "info";
      case CREDIT_NOTE_STATUS_RESEND_REQUESTED:
        return "warning";
      default:
        return "info";
    }
  };

  let xmlButton = null;
  let pdfButton = null;

  if (creditNote.xmlPath !== undefined) {
    xmlButton = (
      <PermissionRequiredComponent permissionName={CREDIT_NOTES_RETRIEVE}>
        <CTooltip content="XML">
          <CButton
            className="text-white"
            color="info"
            onClick={() => {
              onXMLClick(creditNote);
            }}
          >
            <i className="fa fa-code"></i>
          </CButton>
        </CTooltip>
      </PermissionRequiredComponent>
    );
  }

  if (creditNote.pdfPath !== undefined) {
    pdfButton = (
      <PermissionRequiredComponent permissionName={CREDIT_NOTES_RETRIEVE}>
        <CTooltip content="PDF">
          <CButton
            className="text-white"
            color="danger"
            onClick={() => {
              onPDFClick(creditNote);
            }}
          >
            <i className="fa fa-file-pdf"></i>
          </CButton>
        </CTooltip>
      </PermissionRequiredComponent>
    );
  }

  return (
    <CCard className="mb-2">
      <CCardHeader
        className="d-flex justify-content-between align-items-center cursor-pointer"
        onClick={() => setOpen(!open)}
        color={getStatusColor(creditNote.status)}
      >
        <span>
          <strong>{formatCreditNoteNumber(creditNote)}</strong>
        </span>
        <span className="d-flex align-items-center gap-2">
          <strong>
            {formatUTCDateTime(new Date(creditNote.creditNoteDate!))}
          </strong>
          <span
            className="rounded-circle bg-white d-flex justify-content-center align-items-center ml-1"
            style={{ width: "22px", height: "22px" }}
          >
            <i
              className={`fa fa-chevron-${open ? "up" : "down"}`}
              style={{ color: "black", fontSize: "12px" }}
            />
          </span>
        </span>
      </CCardHeader>
      <CCollapse show={open}>
        <CCardBody>
          <p>
            <strong>Razón Social:</strong> {creditNote.clientSocialName}
          </p>
          <p>
            <strong>RUC:</strong> {creditNote.clientRuc}
          </p>
          <p>
            <strong>Monto:</strong>{" "}
            {formatToCurrency(creditNote.amount!, creditNote.currency!)}
          </p>
          <p>
            <strong>Moneda:</strong>{" "}
            {CURRENCY_CHOICES.get(creditNote.currency!)}
          </p>
          <p>
            <strong>Timbrado Usado:</strong> {creditNote.stampIdentifier}
          </p>

          <p>
            <strong>Estado:</strong>{" "}
            <CreditNoteCrudStatus creditNote={creditNote} />
          </p>
          <div>
            <strong>Archivos:</strong>{" "}
            <td className="text-center">
              <CButtonGroup>
                {xmlButton}
                {pdfButton}
                {creditNote.pdfPath && creditNote.id !== undefined && <DocumentPrintButton kind="credit-note" documentId={creditNote.id} />}
              </CButtonGroup>
            </td>
          </div>
          <div>
            <strong>Acciones disponibles:</strong>{" "}
            <CreditNoteCrudActions
              creditNote={creditNote}
              onCancellationClick={onCancellationClick}
              onCancellationRetryClick={onCancellationRetryClick}
              onCancellationRevertClick={onCancellationRevertClick}
              onReadClick={onReadClick}
              onUpdateClick={onUpdateClick}
              onDeleteClick={onDeleteClick}
              onConsultByCdcClick={onConsultByCdcClick}
              onResendClick={onResendClick}
              onSendEmailClick={onSendEmailClick}
              onHistoryClick={onHistoryClick}
            />
          </div>
        </CCardBody>
      </CCollapse>
    </CCard>
  );
}
